export type LocalizedField = { en?: string; ar?: string } | string | null | undefined;

/**
 * Reads a field that may be localized, may be a plain string, or may be missing.
 *
 * Several models disagree about which they are — a lead's `name` is a plain string while
 * a company's is `{ en, ar }` — so call sites were reaching for `.en` on values the type
 * says are strings, and TypeScript was right to object.
 */
export function getLocalizedValue(field?: LocalizedField): string {
  if (!field) return '';
  if (typeof field === 'string') return field.trim();
  return (field.en ?? field.ar ?? '').trim();
}

/**
 * The same, for values wrapped in a `{ name }` object.
 *
 * `transactions.model` carries two product snapshots whose shapes differ: on one,
 * `carModel` is the localized field itself; on the other it is `{ name: LanguageField }`.
 * Call sites tried both with `??`, which meant one arm was always impossible.
 */
export function getLocalizedName(
  field?: { en?: string; ar?: string; name?: LocalizedField } | string | null
): string {
  if (!field) return '';
  if (typeof field === 'string') return field.trim();
  // A single shape covering both, because `name` being optional means `in` cannot
  // discriminate the union.
  if (field.name !== undefined) return getLocalizedValue(field.name);
  return getLocalizedValue({ en: field.en, ar: field.ar });
}

export function formatJoinedLabel(parts: (string | null | undefined)[], separator = ' - '): string {
  const cleaned = parts.map((part) => (part ?? '').trim()).filter(Boolean);
  return cleaned.join(separator);
}

export function formatBranchLocationLabel(
  branch?: {
    branchCity?: LocalizedField;
    branchArea?: LocalizedField;
    address?: string;
  } | null
): string {
  if (!branch) return '';

  const city = getLocalizedValue(branch.branchCity);
  const area = getLocalizedValue(branch.branchArea);
  const cityAreaLabel = city && area ? formatJoinedLabel([city, area]) : city || area;

  return cityAreaLabel || (branch.address ?? '').trim();
}

export function formatDisplayLabel(value?: string | number | null, fallback = '--'): string {
  if (value == null) return fallback;
  if (typeof value === 'number') return String(value);

  const trimmed = value.trim();
  if (!trimmed) return fallback;

  const parts = trimmed
    .split(/\s*-\s*/)
    .map((part) => part.trim())
    .filter((part) => part && part !== 'undefined' && part !== 'null');

  if (parts.length === 0) return fallback;
  return parts.join(' - ');
}
